import React, { Children, useRef, useEffect } from 'react'
import { SliderAnimation, sliderTransition } from './SliderAnimation'
import { twMerge } from 'tailwind-merge'
import { motion } from 'framer-motion'

type SliderContentProps = {
  selectedSlide: number
  children?: React.ReactNode
  className?: string
  addMarginAndSoftenEdges?: boolean
}

export const SliderContent = (props: SliderContentProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const firstRun = useRef(true)

  // When navigating to a step, always reset the scroll
  useEffect(() => {
    if (!ref?.current) return
    // setting scrolltop causes an expensive layout shift
    // paint, so we don't do this on first mount, because
    // we know the scrolltop is 0
    if (firstRun) {
      firstRun.current = false
      return
    }
    ref.current.scrollTop = 0
  }, [props.selectedSlide])

  return (
    <motion.div
      // layout so we can respond to changes in the slider content size
      // layout is also set on the slider animation in the children
      layout
      layoutRoot
      // Match the transition that happens on the child
      transition={sliderTransition}
      ref={ref}
      className={twMerge(
        'relative flex grow overflow-clip shrink min-h-0',
        // When we have soft edges add 2rem of padding, and add
        // negative margin of the same so layout is unaffected
        props.addMarginAndSoftenEdges && '-m-8 p-8',
        props.className,
      )}
      // When we have soft edges, we mask the 2rem of the outside edges
      // so there's no a harsh clip as content slides out of the fram
      style={
        props.addMarginAndSoftenEdges
          ? {
              maskComposite: 'intersect',
              maskImage: `
              linear-gradient(to right, transparent, black 2rem),
              linear-gradient(to left, transparent, black 2rem),
              linear-gradient(to top, transparent, black 2rem),
              linear-gradient(to bottom, transparent, black 2rem)
         `,
            }
          : undefined
      }>
      {Children.map(
        Children.toArray(props.children).filter((child) => !!child),
        (child, i) => (
          <SliderAnimation
            selectedSlide={props.selectedSlide}
            className={props.className}
            key={i}
            order={i}>
            {child}
          </SliderAnimation>
        ),
      )}
    </motion.div>
  )
}
